import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Plus, Trash2, Image as ImageIcon, Upload } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import ImageSuggestionModal from '../../../components/admin/ImageSuggestionModal';
import BunnyLibraryModal from '../../../components/admin/BunnyLibraryModal';
import TranslateButton from '../../../components/admin/TranslateButton';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';
import { uploadImage } from '../../../services/imageService';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsightsForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [currentArticleIndex, setCurrentArticleIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    label_pt: 'INSIGHTS & ATUALIZAÇÕES',
    label_en: 'INSIGHTS & UPDATES',
    heading_pt: '',
    heading_en: '',
    view_all_text_pt: 'Ver Todos os Artigos',
    view_all_text_en: 'View All Articles',
    articles: []
  });

  useEffect(() => {
    loadInsightsData();
  }, []);

  const loadInsightsData = async () => {
    setLoading(true);
    try {
      const [ptResponse, enResponse] = await Promise.all([
        axios.get(`${API_URL}/api/homepage-ml/insights?lang=pt`),
        axios.get(`${API_URL}/api/homepage-ml/insights?lang=en`)
      ]);

      const ptData = ptResponse.data;
      const enData = enResponse.data;

      setFormData({
        label_pt: ptData.label || 'INSIGHTS & ATUALIZAÇÕES',
        label_en: enData.label || 'INSIGHTS & UPDATES',
        heading_pt: ptData.heading || '',
        heading_en: enData.heading || '',
        view_all_text_pt: ptData.view_all_text || 'Ver Todos os Artigos',
        view_all_text_en: enData.view_all_text || 'View All Articles',
        articles: ptData.articles || []
      });
    } catch (error) {
      console.error('Failed to load insights:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArticleChange = (index, field, value) => {
    setFormData(prev => {
      const newArticles = [...prev.articles];
      newArticles[index] = { ...newArticles[index], [field]: value };
      return { ...prev, articles: newArticles };
    });
  };

  const addArticle = () => {
    setFormData(prev => ({
      ...prev,
      articles: [
        ...prev.articles,
        {
          id: prev.articles.length + 1,
          category_pt: '',
          category_en: '',
          title_pt: '',
          title_en: '',
          excerpt_pt: '',
          excerpt_en: '',
          image: '',
          date: new Date().toISOString().split('T')[0],
          link: ''
        }
      ]
    }));
  };

  const removeArticle = (index) => {
    setFormData(prev => ({
      ...prev,
      articles: prev.articles.filter((_, i) => i !== index)
    }));
  };

  const handleImageSelect = (url) => {
    if (currentArticleIndex !== null) {
      handleArticleChange(currentArticleIndex, 'image', url);
      toast({ title: "Imagem selecionada" });
    }
  };

  const handleFileUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ variant: "destructive", title: "Erro", description: "Selecione uma imagem válida" });
      return;
    }

    setUploading(true);
    try {
      const result = await uploadImage(file, 'images/insights');
      if (result.success) {
        handleArticleChange(index, 'image', result.data.cdn_url);
        toast({ title: "Upload completo" });
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Erro no upload" });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/api/homepage-ml/insights`, formData);
      toast({ title: "Sucesso", description: "Secção Insights guardada" });
    } catch (error) {
      toast({ variant: "destructive", title: "Erro", description: "Falha ao guardar" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Secção Insights</CardTitle>
            <CardDescription>Configure artigos e atualizações (PT/EN)</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Portuguese */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#00B4D8]">🇵🇹 Português</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Label (PT)</label>
                    <input
                      type="text"
                      value={formData.label_pt}
                      onChange={(e) => handleInputChange('label_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Heading (PT)</label>
                    <input
                      type="text"
                      value={formData.heading_pt}
                      onChange={(e) => handleInputChange('heading_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Texto "Ver Todos" (PT)</label>
                    <input
                      type="text"
                      value={formData.view_all_text_pt}
                      onChange={(e) => handleInputChange('view_all_text_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* English */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#00B4D8]">🇬🇧 English</h3>
                  <TranslateButton
                    sourceTexts={{
                      label: formData.label_pt,
                      heading: formData.heading_pt,
                      view_all_text: formData.view_all_text_pt
                    }}
                    onTranslated={(translated) => {
                      setFormData(prev => ({
                        ...prev,
                        label_en: translated.label || '',
                        heading_en: translated.heading || '',
                        view_all_text_en: translated.view_all_text || ''
                      }));
                    }}
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Label (EN)</label>
                    <input
                      type="text"
                      value={formData.label_en}
                      onChange={(e) => handleInputChange('label_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Heading (EN)</label>
                    <input
                      type="text"
                      value={formData.heading_en}
                      onChange={(e) => handleInputChange('heading_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">"View All" Text (EN)</label>
                    <input
                      type="text"
                      value={formData.view_all_text_en}
                      onChange={(e) => handleInputChange('view_all_text_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Articles List */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Artigos</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addArticle}>
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Artigo
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.articles.map((article, idx) => (
                    <Card key={idx} className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-medium">Artigo {idx + 1}</h4>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeArticle(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Image */}
                      {article.image && (
                        <div className="relative mb-3">
                          <img src={article.image} alt="Preview" className="w-full h-32 object-cover rounded" />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleArticleChange(idx, 'image', '')}
                            className="absolute top-2 right-2"
                          >
                            Remover
                          </Button>
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div>
                          <input type="file" id={`article-upload-${idx}`} accept="image/*" onChange={(e) => handleFileUpload(e, idx)} className="hidden" />
                          <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => document.getElementById(`article-upload-${idx}`).click()}>
                            <Upload className="w-4 h-4 mr-1" />
                            Upload
                          </Button>
                        </div>
                        <Button type="button" variant="outline" size="sm" className="w-full border-[#00B4D8] text-[#00B4D8] hover:bg-[#E0F2FE]" onClick={() => { setCurrentArticleIndex(idx); setLibraryOpen(true); }}>
                          <ImageIcon className="w-4 h-4 mr-1" />
                          Biblioteca
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => { setCurrentArticleIndex(idx); setSuggestionsOpen(true); }}>
                          <ImageIcon className="w-4 h-4 mr-1" />
                          Sugestões
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={article.category_pt}
                          onChange={(e) => handleArticleChange(idx, 'category_pt', e.target.value)}
                          placeholder="Categoria PT"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={article.category_en}
                          onChange={(e) => handleArticleChange(idx, 'category_en', e.target.value)}
                          placeholder="Category EN"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={article.title_pt}
                          onChange={(e) => handleArticleChange(idx, 'title_pt', e.target.value)}
                          placeholder="Título PT"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={article.title_en}
                          onChange={(e) => handleArticleChange(idx, 'title_en', e.target.value)}
                          placeholder="Title EN"
                          className="px-3 py-2 border rounded"
                        />
                        <textarea
                          value={article.excerpt_pt}
                          onChange={(e) => handleArticleChange(idx, 'excerpt_pt', e.target.value)}
                          placeholder="Resumo PT"
                          rows={2}
                          className="px-3 py-2 border rounded col-span-2"
                        />
                        <textarea
                          value={article.excerpt_en}
                          onChange={(e) => handleArticleChange(idx, 'excerpt_en', e.target.value)}
                          placeholder="Excerpt EN"
                          rows={2}
                          className="px-3 py-2 border rounded col-span-2"
                        />
                        <input
                          type="date"
                          value={article.date}
                          onChange={(e) => handleArticleChange(idx, 'date', e.target.value)}
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={article.link}
                          onChange={(e) => handleArticleChange(idx, 'link', e.target.value)}
                          placeholder="Link"
                          className="px-3 py-2 border rounded"
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="pt-6 border-t space-y-4">
                <AutoTranslateIndicator />
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-[#00B4D8]" data-testid="insights-save-button">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><Save className="w-4 h-4 mr-2" />Guardar</>}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <ImageSuggestionModal
        category="insights"
        isOpen={suggestionsOpen}
        onClose={() => setSuggestionsOpen(false)}
        onSelect={handleImageSelect}
      />

      <BunnyLibraryModal
        folder="insights"
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleImageSelect}
      />
    </div>
  );
};

export default InsightsForm;